import React, { useMemo, useState } from 'react'

import { Modal } from 'antd'
import useSWR from 'swr'

import { CommonTaskStatus } from '@clinic/client'
import { getClusterDataStatus, getLogUrl, rebuildClusterData } from '~/api'
import LoadingSpin from '~/components/LoadingSpin'
import { useMutation } from '~/hooks/use-mutation'
import { clusterDetailTelemetry } from '~/telemetry'
import { formatDateTime } from '~/utils/date-time'

import { DetailItemCard } from '../components/DetailItemCard'
import { ItemModalProps, ItemType } from './types'

const InstanceLogsModalBody: React.FC<ItemModalProps> = ({
  oid,
  cid,
  timeRange
}) => {
  const { start, end } = timeRange
  const itemType = ItemType.InstanceLogs

  // invalidate the cache
  const now = useMemo(() => new Date().valueOf(), [])
  const {
    data: dataStatusRes,
    error,
    mutate
  } = useSWR(
    [`/orgs/${oid}/clusters/${cid}/data_status`, start, end, itemType, now],
    () => getClusterDataStatus(oid, cid, start, end, itemType)
  )
  const loading = !dataStatusRes && !error

  const { run: runRebuildClusterData } = useMutation(rebuildClusterData)

  // instance log & slow query log
  function handleDataItem(i: CommonTaskStatus) {
    if (i.status === -1) {
      Modal.confirm({
        title: 'Do you want to rebuild the data?',
        content: 'The data is cold, need to rebuild before ready',
        onOk: () => {
          runRebuildClusterData(
            {
              oid,
              cid,
              rebuild: { dataType: itemType, itemID: i.itemID }
            },
            function () {
              mutate()
            }
          )
        }
      })
    } else if (i.status === 0) {
      Modal.info({
        title: 'Data rebuild is queued',
        content: 'It may need several minutes to start rebuilding'
      })
      mutate()
    } else if (i.status === -2) {
      Modal.info({
        title: 'Rebuild failed or data expired',
        content: `Rebuild failed or data expired`
      })
      mutate()
    } else if (i.status! < 100) {
      Modal.info({
        title: 'The data is still rebuilding',
        content: `Please wait untill the data is ready (process: ${i.status}%)`
      })
      mutate()
    } else {
      // i.status === 100
      // itemType === 2
      getLogUrl(oid, cid, i.itemID!, start, end)
        .then((res) => {
          clusterDetailTelemetry.clickInstanceLogsItem(i.itemID!)

          let fullUrl = res.data.url!
          if (fullUrl.startsWith('/')) {
            fullUrl = `${window.location.protocol}//${window.location.host}${fullUrl}`
          }
          window.open(fullUrl, '_blank')
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }

  if (loading) {
    return (
      <div className='text-center'>
        <LoadingSpin />
      </div>
    )
  }

  // instance log & slow query log
  return (
    <div className='flex flex-col items-center'>
      {dataStatusRes?.data.items?.map((i) => (
        <a key={i.itemID} onClick={() => handleDataItem(i)}>
          {formatDateTime(i.startTime!)} - {formatDateTime(i.endTime!)}
        </a>
      ))}
    </div>
  )
}

export const InstanceLogsModal: React.FC<ItemModalProps> = ({
  oid,
  cid,
  timeRange
}) => {
  const name = 'Instance Logs'
  const [modalVisible, setModalVisible] = useState(false)
  return (
    <>
      <DetailItemCard
        name={name}
        onClick={() => {
          clusterDetailTelemetry.clickLogsCard()
          setModalVisible(true)
        }}
      />

      <Modal
        visible={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        title={name}
        destroyOnClose={true}
      >
        <InstanceLogsModalBody
          oid={oid}
          cid={cid}
          timeRange={timeRange}
          onClose={() => setModalVisible(false)}
        />
      </Modal>
    </>
  )
}
